import { times } from 'ramda';
import { i18n } from 'i18n';
import { isNumber, round } from 'utils';
import { combineRowsToCsv, CsvRow, downloadCsv } from 'modules/project/ProjectAreaPage/ProjectReportsPage/reportCsv';

export interface HglPoint {
  x: number;
  y: number;
  kpa?: number;
}

export interface HglCsvSeries {
  name: string;
  data: HglPoint[];
}

export interface HglCsvContext {
  series: HglCsvSeries[];
  // Choke stations and rupture disks are drawn as annotation points rather than series, but they are on the
  // graph and carry data, so they export as their own columns.
  markerGroups: HglCsvSeries[];
  chokeHeadByDistance: Map<number, number>;
  distanceAbbr: string;
  // The single series plotted against the secondary axis, so its column carries a pressure unit, not a distance.
  pressureSeriesName: string;
  pressureAbbr: string;
  stopeName?: string;
}

const VALUE_DECIMALS = 3;
const KPA_DECIMALS = 1;
const KPA_ABBR = 'kPa';

interface Row {
  x: number;
  occurrence: number;
}

interface Column {
  header: string;
  decimals: number;
  valueAt: (row: Row) => number | undefined;
}

interface GroupedSeries {
  name: string;
  byDistance: Map<number, HglPoint[]>;
}

const isPlotted = (point: HglPoint) => isNumber(point?.x) && isNumber(point?.y);

// Lines are built stope -> plant. Reverse rather than sort, or each step's two points come out backwards.
const inPlantToStopeOrder = (points: HglPoint[]): HglPoint[] => {
  const plotted = points.filter(isPlotted);
  return plotted.length > 1 && plotted[0].x > plotted[plotted.length - 1].x ? [...plotted].reverse() : plotted;
};

const groupSeries = ({ name, data }: HglCsvSeries): GroupedSeries => ({
  name,
  byDistance: inPlantToStopeOrder(data)
    .reduce((acc, point) => acc.set(point.x, [...(acc.get(point.x) ?? []), point]), new Map<number, HglPoint[]>()),
});

const pointAt = ({ byDistance }: GroupedSeries, { x, occurrence }: Row): HglPoint | undefined => {
  const points = byDistance.get(x);
  return points ? points[Math.min(occurrence, points.length - 1)] : undefined;
};

const hasKpa = ({ byDistance }: GroupedSeries) => Array.from(byDistance.values())
  .some((points) => points.some((point) => isNumber(point.kpa)));

// Series names are unique by construction, but a duplicate would collapse two columns into one, so headers
// are numbered rather than overwritten.
const createHeaderFactory = () => {
  const used = new Map<string, number>();
  return (header: string): string => {
    const seen = used.get(header) ?? 0;
    used.set(header, seen + 1);
    return seen === 0 ? header : `${header} ${seen + 1}`;
  };
};

const buildColumns = (entries: GroupedSeries[], context: HglCsvContext, nextHeader: (header: string) => string): Column[] => {
  const {
    chokeHeadByDistance, distanceAbbr, pressureSeriesName, pressureAbbr,
  } = context;

  const unitFor = (name: string) => (name === pressureSeriesName ? pressureAbbr : distanceAbbr);

  const withPressure = (entry: GroupedSeries): Column[] => [
    { header: nextHeader(`${entry.name} (${unitFor(entry.name)})`), decimals: VALUE_DECIMALS, valueAt: (row) => pointAt(entry, row)?.y },
    ...(hasKpa(entry)
      ? [{ header: nextHeader(`${entry.name} (${KPA_ABBR})`), decimals: KPA_DECIMALS, valueAt: (row: Row) => pointAt(entry, row)?.kpa }]
      : []),
  ];

  return [
    ...entries.flatMap(withPressure),
    ...(chokeHeadByDistance.size > 0
      ? [{ header: nextHeader(`${i18n._('Choke Station Head')} (${distanceAbbr})`), decimals: VALUE_DECIMALS, valueAt: ({ x }: Row) => chokeHeadByDistance.get(x) }]
      : []),
  ];
};

export const buildHglCsvRows = (context: HglCsvContext): CsvRow[] => {
  const nextHeader = createHeaderFactory();
  const distanceHeader = nextHeader(`${i18n._('Distance from Pump ').trim()} (${context.distanceAbbr})`);
  const entries = [...context.series, ...context.markerGroups].map(groupSeries);
  const columns = buildColumns(entries, context, nextHeader);

  const rowsPerDistance = entries.reduce((acc, { byDistance }) => {
    byDistance.forEach((points, x) => acc.set(x, Math.max(acc.get(x) ?? 0, points.length)));
    return acc;
  }, new Map<number, number>());

  const rows = Array.from(rowsPerDistance.entries())
    .sort(([a], [b]) => a - b)
    .flatMap(([x, count]) => times((occurrence): Row => ({ x, occurrence }), count));

  return rows.map((row) => columns.reduce<CsvRow>((acc, column) => {
    const value = column.valueAt(row);
    acc[column.header] = isNumber(value) ? round(value, column.decimals) : '';
    return acc;
  }, { [distanceHeader]: round(row.x, VALUE_DECIMALS) }));
};

const sanitiseFilenamePart = (value: string) => value
  .trim()
  .replace(/[^a-zA-Z0-9-_]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 60);

export const buildHglFilename = (extension: string, stopeName?: string): string => {
  const part = stopeName ? sanitiseFilenamePart(stopeName) : '';
  return part ? `hydraulic-grade-line-${part}.${extension}` : `hydraulic-grade-line.${extension}`;
};

export const buildHglCsvFilename = (stopeName?: string): string => buildHglFilename('csv', stopeName);

export const exportHglCsv = (context: HglCsvContext): void => {
  const rows = buildHglCsvRows(context);
  if (rows.length === 0) return;
  downloadCsv(buildHglCsvFilename(context.stopeName), combineRowsToCsv(rows));
};
